import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('pricemap_token');
    const savedUser = localStorage.getItem('pricemap_user');
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem('pricemap_token');
        localStorage.removeItem('pricemap_user');
      }
    }
    setLoading(false);
  }, []);

  // Register with email
  const register = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'რეგისტრაცია ვერ მოხერხდა');
      }
      
      return response.json();
    } catch (error) {
      const message = error?.message || 'რეგისტრაცია ვერ მოხერხდა';
      throw new Error(message);
    }
  };

  // Verify email with code
  const verifyEmail = async (email, code) => {
    const response = await fetch(`${API_URL}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'ვერიფიკაცია ვერ მოხერხდა');
    }
    
    const data = await response.json();
    setToken(data.access_token);
    setUser(data.user);
    setIsAuthenticated(true);
    localStorage.setItem('pricemap_token', data.access_token);
    localStorage.setItem('pricemap_user', JSON.stringify(data.user));
    return data;
  };

  // Resend verification code
  const resendVerification = async (email) => {
    const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'კოდის გაგზავნა ვერ მოხერხდა');
    }
    
    return response.json();
  };

  // Login with email/password
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'შესვლა ვერ მოხერხდა');
      }
      
      const data = await response.json();
      setToken(data.access_token);
      setUser(data.user);
      setIsAuthenticated(true);
      localStorage.setItem('pricemap_token', data.access_token);
      localStorage.setItem('pricemap_user', JSON.stringify(data.user));
      return data;
    } catch (error) {
      // Ensure we only throw string messages
      const message = error?.message || 'შესვლა ვერ მოხერხდა';
      throw new Error(message);
    }
  };

  // Google OAuth login
  const googleLogin = async (credential, terms_accepted = false) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, terms_accepted })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Google შესვლა ვერ მოხერხდა');
      }

      const data = await response.json();

      // New user — terms not yet accepted
      if (data.needs_terms) {
        const err = new Error('NEEDS_TERMS');
        err.googleName = data.name;
        err.googleEmail = data.email;
        throw err;
      }

      setToken(data.access_token);
      setUser(data.user);
      setIsAuthenticated(true);
      localStorage.setItem('pricemap_token', data.access_token);
      localStorage.setItem('pricemap_user', JSON.stringify(data.user));
      return data;
    } catch (error) {
      const message = error?.message || 'Google შესვლა ვერ მოხერხდა';
      const err = new Error(message);
      if (error.googleName) err.googleName = error.googleName;
      if (error.googleEmail) err.googleEmail = error.googleEmail;
      throw err;
    }
  };

  // Delete account
  const deleteAccount = async () => {
    const response = await fetch(`${API_URL}/api/auth/delete-account`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'ანგარიშის წაშლა ვერ მოხერხდა');
    }
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('pricemap_token');
    localStorage.removeItem('pricemap_user');
    window.location.href = '/';
  };

  // Logout
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('pricemap_token');
    localStorage.removeItem('pricemap_user');
    // Redirect to home after logout
    window.location.href = '/';
  };

  // Update profile
  const updateProfile = async (data) => {
    const response = await fetch(`${API_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'პროფილის განახლება ვერ მოხერხდა');
    }
    
    const updatedUser = await response.json();
    setUser(updatedUser);
    localStorage.setItem('pricemap_user', JSON.stringify(updatedUser));
    return updatedUser;
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('pricemap_user', JSON.stringify(userData));
      } else {
        logout();
      }
    } catch (e) {
      console.error('Failed to refresh user:', e);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    register,
    verifyEmail,
    resendVerification,
    login,
    googleLogin,
    logout,
    deleteAccount,
    updateProfile,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
